/**
 * audioLangPreference — remember & auto-apply audio-track language per playlist.
 *
 * v1.3.97 — Devices with providers that default to a non-English audio
 * track had to open the audio-track picker every episode. This helper
 * persists the language code the user chose (localStorage, key
 * `vupp_pref_audio_lang__<playlist_id>`) and applies it automatically
 * on the next `loadedmetadata` / HLS.js `manifest-parsed`.
 *
 * Language matching is best-effort:
 *   • Exact match on the ISO-639 code first (e.g. "eng" == "eng")
 *   • Case-insensitive
 *   • Falls back to a prefix match ("en" matches "eng")
 * If nothing matches we don't touch anything — the stream keeps its
 * server-signalled default track.
 */

function activePlaylistId() {
  try { return localStorage.getItem("vupp_active_playlist") || null; }
  catch { return null; }
}

export function readPreferredAudioLang() {
  const pid = activePlaylistId();
  if (!pid) return null;
  try { return localStorage.getItem(`vupp_pref_audio_lang__${pid}`) || null; }
  catch { return null; }
}

export function writePreferredAudioLang(lang) {
  const pid = activePlaylistId();
  if (!pid || !lang) return;
  try { localStorage.setItem(`vupp_pref_audio_lang__${pid}`, lang); }
  catch { /* quota */ }
}

// Language matcher used by both auto-apply paths.
function langMatches(a, b) {
  if (!a || !b) return false;
  const la = String(a).toLowerCase();
  const lb = String(b).toLowerCase();
  if (la === lb) return true;
  // Prefix tolerance: "en" ↔ "eng", "pt" ↔ "por", etc.
  return la.startsWith(lb) || lb.startsWith(la);
}

/**
 * Apply the persisted preference (if any) to the current video +
 * optional HLS.js instance. Safe to call multiple times — if the
 * matching track is already selected, this is a no-op.
 *
 * Returns true when a track was selected, false otherwise.
 */
export function applyPreferredAudioLang(videoEl, hlsInstance) {
  const pref = readPreferredAudioLang();
  if (!pref) return false;

  // HLS.js path — hlsInstance.audioTracks[].lang.
  if (hlsInstance?.audioTracks?.length) {
    const hit = hlsInstance.audioTracks.find((t) => langMatches(t.lang, pref));
    if (hit && hlsInstance.audioTrack !== hit.id) {
      hlsInstance.audioTrack = hit.id;
      return true;
    }
    return false;
  }

  // Native path — HTMLMediaElement.audioTracks[].language.
  const list = videoEl?.audioTracks;
  if (list?.length) {
    const arr = Array.from(list);
    const hit = arr.find((t) => langMatches(t.language, pref));
    if (hit && !hit.enabled) {
      arr.forEach((t) => { t.enabled = t === hit; });
      return true;
    }
  }
  return false;
}
